import { useMemo, useState } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { EntityFieldOption, EntityGroup, EntityValue } from '@erp/shared';
import { ChevronDown } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useUpdateTicketEntities } from './tickets-mutations';
import { errorMessage } from './ticket-format';

function ticketEntitiesQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'entities', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EntityValue[]>(`/tickets/${ticketNumber}/entities`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

function ticketEntityGroupsQuery() {
  return queryOptions({
    queryKey: ['erp', 'entities', 'groups', 'ticket'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EntityGroup[]>('/entities', {
        query: { applied_in: 'ticket' },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

interface DraftValue {
  value: string | null;
  optionId: number | null;
}

/** Card colapsável "Informações adicionais" — campos personalizados do ticket. */
export function TicketEntitiesCard({
  ticketNumber,
  readOnly = false,
}: {
  ticketNumber: number;
  /** spec 011: ticket espelhado — os campos aparecem, mas não se editam */
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const values = useQuery({ ...ticketEntitiesQuery(ticketNumber), enabled: open });
  const groups = useQuery({ ...ticketEntityGroupsQuery(), enabled: open });
  const save = useUpdateTicketEntities(ticketNumber);
  const [draft, setDraft] = useState<Record<number, DraftValue>>({});

  const optionsByField = useMemo(() => {
    const map = new Map<number, EntityFieldOption[]>();
    for (const g of groups.data ?? []) {
      for (const f of g.fields) map.set(f.id, f.options);
    }
    return map;
  }, [groups.data]);

  const grouped = useMemo(() => {
    const map = new Map<string, EntityValue[]>();
    for (const v of values.data ?? []) {
      const list = map.get(v.groupName);
      if (list) list.push(v);
      else map.set(v.groupName, [v]);
    }
    return [...map.entries()];
  }, [values.data]);

  const setField = (original: EntityValue, next: DraftValue) => {
    setDraft((prev) => {
      const clone = { ...prev };
      const sameValue = (next.value ?? null) === (original.value ?? null);
      const sameOption = (next.optionId ?? null) === (original.optionId ?? null);
      if (sameValue && sameOption) delete clone[original.fieldId];
      else clone[original.fieldId] = next;
      return clone;
    });
  };

  const dirty = Object.keys(draft).length > 0;

  const submit = () => {
    save.mutate(
      {
        values: Object.entries(draft).map(([fieldId, v]) => ({
          fieldId: Number(fieldId),
          value: v.value,
          optionId: v.optionId,
        })),
      },
      { onSuccess: () => setDraft({}) }
    );
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border bg-card text-card-foreground shadow-sm"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-4 text-left">
        <h2 className="text-base font-semibold">Informações adicionais</h2>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        {values.isPending || groups.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : values.isError ? (
          <p className="text-sm text-destructive">
            Erro ao carregar campos: {errorMessage(values.error)}
          </p>
        ) : grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum campo personalizado configurado para tickets.
          </p>
        ) : (
          // fieldset desabilitado desliga TODOS os controles de uma vez — e sem
          // edição o rascunho nunca fica "dirty", então Salvar nem aparece
          <fieldset disabled={readOnly} className="min-w-0 space-y-4">
            {grouped.map(([groupName, fields]) => (
              <div key={groupName}>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{groupName}</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {fields.map((v) => {
                    const current = draft[v.fieldId] ?? { value: v.value, optionId: v.optionId };
                    return (
                      <div key={v.fieldId} className="space-y-1">
                        <Label className="text-sm font-semibold">
                          {v.fieldName}
                          {v.required ? ' *' : ''}
                        </Label>
                        <EntityFieldControl
                          field={v}
                          current={current}
                          options={optionsByField.get(v.fieldId) ?? []}
                          onChange={(next) => setField(v, next)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {save.isError ? (
              <p className="text-sm text-destructive">{errorMessage(save.error)}</p>
            ) : null}
            {dirty ? (
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={save.isPending}
                  onClick={() => setDraft({})}
                >
                  Descartar
                </Button>
                <Button size="sm" disabled={save.isPending} onClick={submit}>
                  {save.isPending ? 'Salvando…' : 'Salvar'}
                </Button>
              </div>
            ) : null}
          </fieldset>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function EntityFieldControl({
  field,
  current,
  options,
  onChange,
}: {
  field: EntityValue;
  current: DraftValue;
  options: EntityFieldOption[];
  onChange: (next: DraftValue) => void;
}) {
  switch (field.fieldType) {
    case 'text_area':
      return (
        <Textarea
          value={current.value ?? ''}
          onChange={(e) => onChange({ value: e.target.value || null, optionId: null })}
        />
      );
    case 'single_select':
      return (
        <Select
          className="h-8"
          value={current.optionId != null ? String(current.optionId) : ''}
          onChange={(e) => {
            const id = e.target.value ? Number(e.target.value) : null;
            const opt = id != null ? options.find((o) => o.id === id) : undefined;
            onChange({ value: opt?.value ?? null, optionId: id });
          }}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.value}
            </option>
          ))}
        </Select>
      );
    case 'checkbox':
      return (
        <div className="flex h-8 items-center">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={current.value === 'true'}
            onChange={(e) =>
              onChange({ value: e.target.checked ? 'true' : 'false', optionId: null })
            }
          />
        </div>
      );
    default: {
      const type =
        field.fieldType === 'date'
          ? 'date'
          : field.fieldType === 'phone'
            ? 'tel'
            : field.fieldType === 'email'
              ? 'email'
              : field.fieldType === 'link'
                ? 'url'
                : 'text';
      return (
        <Input
          type={type}
          className="h-8"
          value={current.value ?? ''}
          placeholder={field.fieldType === 'currency' ? 'R$ 0,00' : undefined}
          onChange={(e) => onChange({ value: e.target.value || null, optionId: null })}
        />
      );
    }
  }
}
